import classNames from 'classnames';
import { motion, useAnimationControls, useMotionValue } from 'framer-motion';
import React, {
    ReactNode,
    RefObject,
    useCallback,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { useThrottledCallback } from 'use-debounce';

import Coordinates from '../../hooks/ui/useGrab/types/Coordinates';
import useGrab from '../../hooks/ui/useGrab/useGrab';
import useWindowResize from '../../hooks/useWindowResize';

import Fades, { defaultFadeWidth } from './components/Fades/Fades';
import useFadeVisible from './hooks/useFadeVisible';
import FadeConfiguration from './types/FadeConfiguration';

import styles from './HorizontalSlider.module.css';

export type ScrollHandles = {
    scrollTo: (x: number) => void;
};

type HorizontalSliderProps = {
    children: ReactNode;
    mode?: 'transform' | 'scroll';
    fade?: FadeConfiguration;
    contentClassName?: string;
    onDragUpdate?: (x: number) => void;
    scrollRef?: RefObject<ScrollHandles>;
};

const HorizontalSlider = ({
    children,
    mode = 'transform',
    fade,
    contentClassName,
    onDragUpdate,
    scrollRef,
}: HorizontalSliderProps) => {
    const constraintsRef = useRef<HTMLDivElement>(null);
    const contentContainerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [isDragging, setDragging] = useState(false);
    const dragX = useMotionValue(0);
    const controls = useAnimationControls();
    const [renderCount, setRenderCount] = useState(0);

    const updateScrollPosition = useThrottledCallback(
        (value: number) => {
            dragX.set(value);
            setRenderCount(renderCount + 1);
            onDragUpdate?.(value);
        },
        100,
        { trailing: true }
    );

    useImperativeHandle(
        scrollRef,
        () => ({
            scrollTo: (position: number) => {
                if (mode === 'scroll') {
                    contentContainerRef.current?.scrollTo({ left: position });
                }
            },
        }),
        []
    );

    const { hideFadeLeft, hideFadeRight, draggable } = useFadeVisible(
        constraintsRef,
        contentRef,
        dragX.get(),
        mode,
        fade?.width ?? defaultFadeWidth
    );

    useWindowResize(() => {
        controls.stop();
        controls.set({ x: 0 });
    });

    const updateScrollPositionFromDrag = useCallback(
        ({ x }: Coordinates) => updateScrollPosition(x),
        [updateScrollPosition]
    );

    useGrab({
        dragStartRef: contentContainerRef,
        movingElementRef: contentContainerRef,
        enableInertia: true,
        disabled: mode !== 'scroll',
        onDragUpdate: updateScrollPositionFromDrag,
    });

    const renderContentBasedOnMode = () => {
        if (mode === 'transform') {
            return (
                <motion.div
                    ref={contentRef}
                    drag={draggable ? 'x' : false}
                    dragConstraints={constraintsRef}
                    animate={controls}
                    className={classNames(
                        styles.Content,
                        styles.Content_transformBased,
                        isDragging && styles.locked,
                        contentClassName
                    )}
                    onDragStart={() => setDragging(true)}
                    onDragEnd={() => setDragging(false)}
                    onUpdate={(latest) => {
                        const x = latest.x;
                        if (typeof x === 'undefined') {
                            throw new Error(
                                'No x value found in latest updated.'
                            );
                        }

                        updateScrollPosition(parseInt(x.toString()));
                    }}
                    onDragTransitionEnd={() => setRenderCount(renderCount + 1)}
                >
                    {children}
                </motion.div>
            );
        }

        return (
            <div
                className={styles.ContentWrapper_scrollBased}
                ref={contentContainerRef}
            >
                <div
                    ref={contentRef}
                    className={classNames(styles.Content, contentClassName)}
                >
                    {children}
                </div>
            </div>
        );
    };

    return (
        <motion.div
            ref={constraintsRef}
            className={classNames(styles.Root, {
                [styles.draggable]: draggable,
                [styles.scrollBased]: mode === 'scroll',
                [styles.transformBased]: mode === 'transform',
            })}
        >
            {renderContentBasedOnMode()}

            <Fades
                {...fade}
                hideLeft={hideFadeLeft}
                hideRight={hideFadeRight}
            />
        </motion.div>
    );
};

export default HorizontalSlider;
